'use client'

import Link from 'next/link'
import type { ReactNode } from 'react'
import { AnimatedEmoji } from '@/components/v/animated-emoji'
import { hapticTap } from '@/lib/telegram/haptics'
import { cn } from '@/lib/utils'

export type ErrorVariant = 'forbidden' | 'session' | 'network' | 'generic' | 'notfound'

function Icon({ variant }: { variant: ErrorVariant }) {
  const s = { stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const
  switch (variant) {
    case 'forbidden':
      return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <rect x="6" y="12.5" width="16" height="11" rx="3.5" {...s} />
          <path d="M9.5 12.5V9.5a4.5 4.5 0 0 1 9 0v3" {...s} />
        </svg>
      )
    case 'session':
      return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <circle cx="14" cy="14" r="9" {...s} />
          <path d="M14 8.5V14l3.5 2.5" {...s} />
        </svg>
      )
    case 'network':
      return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <path
            d="M5 11.5C10.2 6.7 17.8 6.7 23 11.5M8.7 15.4c3-2.7 7.6-2.7 10.6 0M12.4 19.2c.9-.8 2.3-.8 3.2 0"
            {...s}
          />
          <path d="M5 5l18 18" {...s} />
        </svg>
      )
    case 'notfound':
      return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="7" {...s} />
          <path d="M17.5 17.5L23 23" {...s} />
        </svg>
      )
    default:
      return (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <path d="M14 5L25 23H3Z" {...s} />
          <path d="M14 12v5" {...s} />
          <circle cx="14" cy="20" r="0.6" fill="currentColor" stroke="currentColor" />
        </svg>
      )
  }
}

const DEFAULT_EMOJI: Record<ErrorVariant, string> = {
  forbidden: '🙈',
  session: '💤',
  network: '📡',
  generic: '🫠',
  notfound: '👻',
}

const TILT: Record<ErrorVariant, number> = { forbidden: -5, session: 4, network: -4, generic: 5, notfound: -3 }

// Whole-screen error / blocked states (CStates 10, 11). Tilted tile + Unbounded
// title + one primary action (button or link).
export function ErrorState({
  variant = 'generic',
  animatedEmoji,
  title,
  body,
  actionLabel,
  onAction,
  href,
  busy,
  extra,
  className,
}: {
  variant?: ErrorVariant
  /** Overrides the per-variant emoji; pass null to use the plain icon tile. */
  animatedEmoji?: string | null
  title: string
  body?: string
  actionLabel?: string
  onAction?: () => void
  href?: string
  busy?: boolean
  extra?: ReactNode
  className?: string
}) {
  const btn =
    'press mt-2 inline-flex items-center justify-center min-h-11 px-6 rounded-full bg-q-text text-q-bg text-[15px] font-bold cursor-pointer disabled:opacity-60'
  return (
    <div className={cn('flex-1 flex flex-col items-center justify-center gap-3 px-8 py-14 text-center', className)}>
      {animatedEmoji === null ? (
        <div
          className="w-[60px] h-[60px] rounded-[20px] bg-q-surface text-q-muted flex items-center justify-center q-fade-up"
          style={{ rotate: `${TILT[variant]}deg` }}
        >
          <Icon variant={variant} />
        </div>
      ) : (
        <AnimatedEmoji emoji={animatedEmoji ?? DEFAULT_EMOJI[variant]} size={96} className="q-fade-up" />
      )}
      <h1
        className="font-display font-bold text-[19px] leading-tight text-q-text q-fade-up"
        style={{ ['--i' as string]: 1 }}
      >
        {title}
      </h1>
      {body ? (
        <p className="text-sm text-q-muted max-w-[300px] leading-snug q-fade-up" style={{ ['--i' as string]: 2 }}>
          {body}
        </p>
      ) : null}
      {actionLabel && href ? (
        <Link href={href as never} onClick={() => hapticTap('light')} className={btn}>
          {actionLabel}
        </Link>
      ) : null}
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction} disabled={busy} className={btn}>
          {actionLabel}
        </button>
      ) : null}
      {extra}
    </div>
  )
}
